// src/utils/GameAudio.js  ─ PLEASANT GAMING AUDIO ENGINE
// Soft, warm, immersive — feels good, not aggressive
// Phase 1 : Gentle cinematic intro chime on game load
// Phase 2 : Calm looping ambient music on login page
// Phase 3 : Soft power-down on successful login

const GA = (() => {
  let ctx          = null;
  let master       = null;
  let loginGain    = null;
  let loginRunning = false;
  let loginTimer   = null;
  let startupDone  = false;
  let dead         = false;

  function boot() {
    if (ctx) { if (ctx.state === 'suspended') ctx.resume().catch(() => {}); return ctx; }
    try {
      ctx    = new (window.AudioContext || window.webkitAudioContext)();
      master = ctx.createGain();
      master.gain.value = 0.28;   // comfortable listening volume
      master.connect(ctx.destination);
    } catch { return null; }
    return ctx;
  }

  // Simple oscillator with smooth attack/release
  function O(freq, dur, type, vol, when, bus, detune) {
    const c = boot(); if (!c) return;
    const t = when ?? c.currentTime;
    const d = bus  ?? master;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type || 'sine';
    o.frequency.setValueAtTime(freq, t);
    if (detune) o.detune.value = detune;
    o.connect(g); g.connect(d);
    const atk = Math.min(dur * 0.12, 0.06);
    g.gain.setValueAtTime(0.001, t);
    g.gain.linearRampToValueAtTime(vol, t + atk);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.start(t);
    o.stop(t + dur + 0.04);
  }

  // Soft filtered noise (used for gentle swells, not sharp hits)
  function N(dur, vol, lpFreq, when, bus) {
    const c = boot(); if (!c) return;
    const t   = when ?? c.currentTime;
    const d   = bus  ?? master;
    const len = Math.floor(c.sampleRate * Math.min(dur, 4));
    const buf = c.createBuffer(1, len, c.sampleRate);
    const dat = buf.getChannelData(0);
    for (let i = 0; i < len; i++) dat[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource(); src.buffer = buf;
    const lp  = c.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = lpFreq || 800;
    const g   = c.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.linearRampToValueAtTime(vol, t + dur * 0.3);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(lp); lp.connect(g); g.connect(d);
    src.start(t);
  }

  // Bell / chime tone — pure sine with long decay (beautiful)
  function BELL(freq, vol, when, bus) {
    const c = boot(); if (!c) return;
    const t = when ?? c.currentTime;
    const d = bus  ?? master;
    // Fundamental
    O(freq,       2.2, 'sine',     vol,       t, d);
    // 2nd harmonic (octave) — softer
    O(freq * 2,   1.6, 'sine',     vol * 0.4, t, d);
    // Slight inharmonic partial for bell character
    O(freq * 2.76, 0.9, 'sine',   vol * 0.15, t, d);
  }

  // Soft pad chord — all sines, very gentle
  function PAD(freqs, dur, vol, when, bus) {
    freqs.forEach((f, i) => {
      O(f, dur, 'sine',     vol,       when, bus, 0);
      O(f, dur, 'triangle', vol * 0.3, when, bus, i % 2 === 0 ? +5 : -5);
    });
  }

  // ── PHASE 1 : GENTLE CINEMATIC INTRO ─────────────────────────────────────
  function playStartup() {
    const c = boot(); if (!c || startupDone) return;
    startupDone = true;
    const t = c.currentTime;

    // Soft rising sparkle — high bells ascending
    const SPARKLE = [523, 659, 784, 1047, 1319, 1568, 2093];
    SPARKLE.forEach((f, i) => {
      BELL(f, 0.055, t + i * 0.11);
    });

    // Warm low pad swell underneath
    PAD([130, 164, 196, 261], 3.2, 0.055, t + 0.2);

    // Second chord — resolves upward (feels hopeful, exciting)
    PAD([164, 207, 261, 329], 2.8, 0.05, t + 0.85);

    // Final bright chord with long tail
    PAD([196, 247, 294, 392, 523], 4.0, 0.055, t + 1.6);

    // Tiny sparkle at the top
    [1047, 1319, 1568, 2093, 2637].forEach((f, i) => {
      BELL(f, 0.03, t + 1.65 + i * 0.08);
    });

    // Soft noise swell (like wind, very quiet)
    N(2.5, 0.018, 600, t + 0.3);
  }

  // ── PHASE 2 : LOGIN AMBIENT LOOP ─────────────────────────────────────────
  // Calm, beautiful, slightly mysterious — good gaming atmosphere
  function startLoginLoop() {
    if (loginRunning || dead) return;
    const c = boot(); if (!c) return;
    loginRunning = true;

    loginGain = c.createGain();
    loginGain.gain.value = 0;
    loginGain.connect(master);
    // Gentle fade-in over 2 seconds
    loginGain.gain.linearRampToValueAtTime(0.72, c.currentTime + 2.0);

    // Musical constants
    const BPM   = 75;            // calm, breathing tempo
    const BEAT  = 60 / BPM;
    const BAR   = BEAT * 4;
    const LOOP  = BAR * 8;       // 8-bar loop

    // Chord progression: Am  F  C  G  (classic, pleasing)
    const CHORDS = [
      [220, 261, 330, 440],   // Am
      [174, 220, 261, 349],   // F
      [261, 329, 392, 523],   // C
      [196, 247, 294, 392],   // G
      [220, 261, 330, 440],   // Am
      [174, 220, 261, 349],   // F
      [196, 247, 294, 392],   // G
      [196, 247, 294, 392],   // G (hold)
    ];

    // Bass notes matching chords
    const BASS = [110, 87, 130, 98, 110, 87, 98, 98];

    // Melody line (pentatonic, sounds pleasant over all chords)
    const MELODY = [
      659, 784, 659, 523,
      698, 659, 523, 440,
      523, 659, 784, 659,
      784, 880, 784, 659,
      659, 784, 659, 523,
      698, 659, 523, 392,
      523, 659, 523, 440,
      392, 440, 523, 392,
    ];

    function scheduleLoop() {
      if (!loginRunning || !ctx || !loginGain) return;
      const ct = ctx.currentTime;

      // Pad chords — each chord lasts one bar, very soft
      CHORDS.forEach((chord, bar) => {
        const w = ct + bar * BAR;
        PAD(chord, BAR * 0.92, 0.13, w, loginGain);
      });

      // Bass line — root note, long smooth notes
      BASS.forEach((f, bar) => {
        const w = ct + bar * BAR;
        O(f,       BAR * 0.85, 'sine',     0.32, w, loginGain, 0);
        O(f * 2,   BAR * 0.75, 'triangle', 0.1,  w, loginGain, 3);
      });

      // Melody — gentle bell-like notes on beat
      MELODY.forEach((f, i) => {
        const w = ct + i * BEAT;
        BELL(f, 0.038, w, loginGain);
        // Add quiet harmony a third below
        const THIRD = f * (2/3) < 200 ? f : f * 0.75;
        O(THIRD, BEAT * 0.55, 'sine', 0.018, w, loginGain);
      });

      // Soft shimmer — very quiet high sparkles on beats
      for (let b = 0; b < 32; b++) {
        if (b % 4 === 0 || b % 4 === 2) {
          const chord = CHORDS[Math.floor(b / 4)];
          const topNote = chord[chord.length - 1] * 2;
          O(topNote, BEAT * 0.3, 'sine', 0.016, ct + b * BEAT, loginGain);
        }
      }

      // Quiet breath noise underneath (atmosphere)
      N(LOOP * 0.9, 0.022, 400, ct + 0.1, loginGain);

      // Schedule next loop ~50ms before end to avoid silence gap
      loginTimer = setTimeout(scheduleLoop, (LOOP - 0.05) * 1000);
    }

    // Start loop after startup sound (2.5s delay)
    setTimeout(scheduleLoop, 2500);
  }

  // ── PHASE 3 : SOFT STOP ON LOGIN ─────────────────────────────────────────
  function stopAll() {
    loginRunning = false;
    dead = true;
    clearTimeout(loginTimer);

    if (!ctx || !loginGain) return;

    // Gentle fade out — like a song ending
    const now = ctx.currentTime;
    loginGain.gain.setValueAtTime(loginGain.gain.value || 0.7, now);
    loginGain.gain.linearRampToValueAtTime(0, now + 1.2);

    // Soft success chime — three ascending bell notes
    setTimeout(() => {
      if (!ctx || !master) return;
      BELL(523,  0.06, ctx.currentTime);
      BELL(659,  0.06, ctx.currentTime + 0.14);
      BELL(1047, 0.07, ctx.currentTime + 0.28);
    }, 100);
  }

  function unlock() { boot(); }

  return { playStartup, startLoginLoop, stopAll, unlock };
})();

export default GA;
